import { Quill } from "react-quill";

const BlockEmbed = Quill.import("blots/block/embed");

export const IFRAME_BLOT_NAME = "ffairIframe";
export const IFRAME_WRAP_CLASS = "ffair-iframe-wrap";
export const IFRAME_CLASS = "ffair-iframe";

export type IframeSizeMode = "percent" | "pixels";

export type IframeData = {
  src: string;
  sizeMode: IframeSizeMode;
  widthPercent?: number;
  widthPx?: number;
  heightPx?: number;
};

const buildWrapStyle = (data: IframeData): string => {
  // `contain: layout paint` isolates the iframe's containing block so that
  // ancestor layout/paint changes (e.g. button hover, tooltip mount) don't
  // trigger reflow inside the iframe and force a redraw of its content
  // (which, for map iframes like OpenStreetMap, looks like a "glitch").
  const base =
    "position:relative;display:block;margin:8px 0;max-width:100%;border-radius:8px;overflow:hidden;border:1px solid #2979FF;box-sizing:border-box;contain:layout paint;";
  if (data.sizeMode === "percent") {
    const width = data.widthPercent ?? 100;
    return `${base}width:${width}%;aspect-ratio:16/9;`;
  }
  const width = data.widthPx ?? 800;
  const height = data.heightPx ?? 450;
  return `${base}width:${width}px;height:${height}px;`;
};

const IFRAME_INLINE_STYLE = "width:100%;height:100%;border:0;display:block;";

class IframeBlot extends BlockEmbed {
  static blotName = IFRAME_BLOT_NAME;

  static tagName = "DIV";

  static className = IFRAME_WRAP_CLASS;

  static create(value: IframeData) {
    const node = super.create(value) as HTMLDivElement;
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-src", value.src ?? "");
    node.setAttribute("data-size-mode", value.sizeMode);
    if (value.sizeMode === "percent") {
      node.setAttribute(
        "data-width-percent",
        String(value.widthPercent ?? 100)
      );
    } else {
      node.setAttribute("data-width-px", String(value.widthPx ?? 800));
      node.setAttribute("data-height-px", String(value.heightPx ?? 450));
    }
    node.setAttribute("style", buildWrapStyle(value));

    const iframe = document.createElement("iframe");
    iframe.classList.add(IFRAME_CLASS);
    iframe.setAttribute("src", value.src ?? "");
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allowfullscreen", "true");
    iframe.setAttribute("style", IFRAME_INLINE_STYLE);
    node.appendChild(iframe);

    return node;
  }

  static value(node: HTMLDivElement): IframeData {
    const sizeMode =
      (node.getAttribute("data-size-mode") as IframeSizeMode) || "pixels";
    return {
      src: node.getAttribute("data-src") || "",
      sizeMode,
      widthPercent:
        Number(node.getAttribute("data-width-percent")) || undefined,
      widthPx: Number(node.getAttribute("data-width-px")) || undefined,
      heightPx: Number(node.getAttribute("data-height-px")) || undefined,
    };
  }
}

export default IframeBlot;
